import { useState, type FormEvent } from "react";

import { INVESTOR_AUDIENCES } from "./Help.js";
import type { OperatingPosture, ReferenceProfileSummary, StartingDataPath } from "./types.js";

export interface OnboardingResult {
  readonly organizationName: string;
  readonly profileKey: string;
  readonly profileVersion: string;
  readonly posture: OperatingPosture;
  readonly startingPath: StartingDataPath;
}

interface OnboardingProps {
  readonly profiles: ReferenceProfileSummary[];
  readonly suggestedName: string;
  readonly submitting: boolean;
  readonly error?: string | undefined;
  readonly profilesUnavailable?: boolean | undefined;
  readonly onRetryProfiles?: (() => void) | undefined;
  readonly onComplete: (result: OnboardingResult) => void;
  readonly onSkip: (result: OnboardingResult) => void;
}

export function Onboarding({
  profiles,
  suggestedName,
  submitting,
  error,
  profilesUnavailable = false,
  onRetryProfiles,
  onComplete,
}: OnboardingProps) {
  const audiences = INVESTOR_AUDIENCES.filter((audience) =>
    profiles.some((profile) => profile.key === audience.key),
  );
  const [chosenProfileKey, setProfileKey] = useState("");
  const profileKey = chosenProfileKey || audiences[0]?.key || "";
  const [organizationName, setOrganizationName] = useState(suggestedName);
  const [posture, setPosture] = useState<OperatingPosture>("evaluating");
  const selected = profiles.find(
    (profile) =>
      profile.key === profileKey && audiences.some((audience) => audience.key === profile.key),
  );
  const ready = selected !== undefined && organizationName.trim().length > 0 && !submitting;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready || selected === undefined) return;
    onComplete({
      organizationName: organizationName.trim(),
      profileKey: selected.key,
      profileVersion: selected.version,
      posture,
      startingPath: "later",
    });
  }

  return (
    <main className="onboard-screen simple-onboarding">
      <header className="onboard-header">
        <p className="eyebrow">POLYFUTURE · WELCOME</p>
      </header>
      <form onSubmit={submit}>
        <section className="onboard-step" aria-labelledby="onboard-profile-heading">
          <h1 id="onboard-profile-heading">Make Polyfuture yours</h1>
          <p>Choose how you invest. Then add your holdings or ask your first question.</p>
          {error === undefined ? null : (
            <p className="entry-error" role="alert">
              {error}
            </p>
          )}
          <ul className="onboard-options" aria-label="How you invest">
            {audiences.map((audience) => (
              <li key={audience.key}>
                <button
                  type="button"
                  className={
                    profileKey === audience.key ? "onboard-option selected" : "onboard-option"
                  }
                  aria-pressed={profileKey === audience.key}
                  onClick={() => setProfileKey(audience.key)}
                  disabled={submitting}
                >
                  <span className="onboard-option-name">{audience.name}</span>
                  <span className="onboard-option-note">{audience.description}</span>
                </button>
              </li>
            ))}
          </ul>
          <p className="onboard-note">
            This sets the information your portfolios need. This choice cannot be changed after
            setup.
          </p>
          <label className="onboard-field">
            <span>
              {profileKey === "individual_investor"
                ? "Name your workspace"
                : "Team or company name"}
            </span>
            <input
              value={organizationName}
              onChange={(event) => setOrganizationName(event.target.value)}
              placeholder="My investments"
              maxLength={200}
              required
              disabled={submitting}
            />
            <small>A workspace keeps your portfolios and conversations together.</small>
          </label>
          {audiences.length === 0 ? (
            <div className="entry-notice" role="status">
              <p>
                {profilesUnavailable
                  ? "We could not load the setup choices. Please try again."
                  : "Setup is not available yet. An administrator needs to enable an investor profile."}
              </p>
              {onRetryProfiles === undefined ? null : (
                <button
                  className="button"
                  type="button"
                  onClick={onRetryProfiles}
                  disabled={submitting}
                >
                  Try again
                </button>
              )}
            </div>
          ) : null}
          <details className="advanced-options">
            <summary>Optional workspace settings</summary>
            <label>
              How will you start?
              <select
                value={posture}
                onChange={(event) => setPosture(event.target.value as OperatingPosture)}
                disabled={submitting}
              >
                <option value="evaluating">Exploring the app</option>
                <option value="production">Using it in my investment work</option>
              </select>
            </label>
            <p>This records how you plan to use the workspace. It does not change your access.</p>
            <p className="onboard-note">
              Profile {selected?.displayName ?? "unavailable"} · {selected?.version}. These profile
              defaults are design assumptions, not a customer-validated assessment of your needs.
            </p>
          </details>
          <p>
            Monitoring starts after you add a portfolio and turn it on. You can explore before then.
          </p>
        </section>
        <footer className="onboard-footer">
          <button className="button button-primary" type="submit" disabled={!ready}>
            {submitting ? "Setting up…" : "Start using Polyfuture"}
          </button>
        </footer>
      </form>
    </main>
  );
}
